import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { CheckCircle, Circle, Sparkles, Camera, BarChart3, Users, Zap, ArrowRight } from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useBranding } from '@/shared/contexts/BrandingContext';
import { useLocation } from 'wouter';
import { trackOnboardingComplete } from '@/utils/analytics';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: any;
  action: string;
  actionPath: string;
  completed: boolean;
  demoAvailable: boolean;
}

export default function StreamlinedOnboarding() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { isAegis360 } = useBranding();

  const [steps, setSteps] = useState<OnboardingStep[]>([
    {
      id: 'demo_ai',
      title: 'Try AI Pest Identification',
      description: 'See the power of our AI with sample pest images',
      icon: Camera,
      action: 'Try Demo',
      actionPath: '/ai-intelligence?demo=true',
      completed: false,
      demoAvailable: true
    },
    {
      id: 'view_analytics',
      title: 'Explore Your Dashboard',
      description: 'See insights with sample business data',
      icon: BarChart3,
      action: 'View Dashboard',
      actionPath: '/performance-dashboard?demo=true',
      completed: false,
      demoAvailable: true
    },
    {
      id: 'add_customer',
      title: 'Add Your First Customer',
      description: 'Start managing your client relationships',
      icon: Users,
      action: 'Add Customer',
      actionPath: '/customer-management',
      completed: false,
      demoAvailable: false
    }
  ]);

  useEffect(() => {
    if (!isAegis360 || !user) return;

    const onboardingStatus = localStorage.getItem('aegis360_streamlined_onboarding');
    if (!onboardingStatus) {
      setTimeout(() => setIsOpen(true), 800);
    } else {
      const savedSteps = JSON.parse(onboardingStatus);
      setSteps(savedSteps);
    }
  }, [isAegis360, user]);

  const completedCount = steps.filter(s => s.completed).length;
  const progressPercentage = (completedCount / steps.length) * 100;

  const markStepComplete = (stepId: string) => {
    const updatedSteps = steps.map(step =>
      step.id === stepId ? { ...step, completed: true } : step
    );
    setSteps(updatedSteps);
    localStorage.setItem('aegis360_streamlined_onboarding', JSON.stringify(updatedSteps));

    // TASK 2: Track onboarding completion and session depth when all steps are done
    if (updatedSteps.every(s => s.completed)) {
      trackOnboardingComplete();
      setTimeout(() => {
        setIsOpen(false);
        localStorage.setItem('aegis360_onboarding_completed', 'true');
      }, 2000);
    }
  };

  const handleStepAction = (step: OnboardingStep, index: number) => {
    setCurrentStep(index);
    markStepComplete(step.id);
    setIsOpen(false);
    navigate(step.actionPath);
  };

  const handleSkipOnboarding = () => {
    localStorage.setItem('aegis360_streamlined_onboarding', JSON.stringify(steps));
    localStorage.setItem('aegis360_onboarding_skipped', 'true');
    setIsOpen(false);
  };

  if (!isAegis360 || !user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[650px] max-w-[95vw]" data-testid="streamlined-onboarding-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-blue-600">
                Welcome to Aegis360!
              </DialogTitle>
              <DialogDescription className="text-base mt-1">
                Get to value in 3 simple steps (under 5 minutes)
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Progress indicator */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Your Progress</span>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                {completedCount}/{steps.length} Complete
              </Badge>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>

          {/* Step cards */}
          <div className="space-y-3">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card
                  key={step.id}
                  className={`transition-all border-2 ${
                    step.completed
                      ? 'border-green-300 bg-green-50'
                      : index === currentStep
                      ? 'border-blue-300 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-blue-200'
                  }`}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 mt-1">
                        {step.completed ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <Circle className="h-6 w-6 text-gray-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-5 w-5 ${
                            step.completed ? 'text-green-600' : 'text-blue-600'
                          }`} />
                          <h4 className="font-semibold text-gray-900">
                            {step.title}
                          </h4>
                          {step.demoAvailable && !step.completed && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                              <Zap className="h-3 w-3 mr-1" />
                              Demo Mode
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{step.description}</p>
                      </div>

                      {!step.completed && (
                        <Button
                          size="sm"
                          onClick={() => handleStepAction(step, index)}
                          className="flex-shrink-0 bg-blue-600 hover:bg-blue-700"
                          data-testid={`button-${step.id}`}
                        >
                          {step.action}
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Value proposition */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-gray-900 mb-1">Why these steps?</p>
                <p className="text-gray-600">
                  Each step shows you real platform value. You'll see AI-powered pest identification, 
                  business analytics, and customer management—the core tools that save pest control 
                  professionals 10+ hours per week.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="ghost"
            onClick={handleSkipOnboarding}
            className="text-gray-500 hover:text-gray-700"
            data-testid="button-skip-onboarding"
          >
            Skip for now
          </Button>
          
          {completedCount === steps.length && (
            <Button
              onClick={() => setIsOpen(false)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="button-complete-onboarding"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Get Started!
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
